import { ProjectRecord, ProjectsFiltersState, FilterChip } from './project-types';
import { getProductGroupLabel, isValidProductGroupKey } from '@/lib/reference-data/product-groups';

export function getCountryCounts(projects: ProjectRecord[]): Record<string, number> {
  return projects.reduce((acc, p) => {
    for (const c of p.country) {
      acc[c] = (acc[c] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
}

export function getOrganizationCounts(projects: ProjectRecord[]): Record<string, number> {
  return projects.reduce((acc, p) => {
    acc[p.organizationName] = (acc[p.organizationName] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

export function getSectorCounts(projects: ProjectRecord[]): Record<string, number> {
  return projects.reduce((acc, p) => {
    acc[p.sector] = (acc[p.sector] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

export function getProductGroupCounts(projects: ProjectRecord[]): Record<string, number> {
  return projects.reduce((acc, p) => {
    if (p.productGroup) {
      acc[p.productGroup] = (acc[p.productGroup] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);
}

export function applyFiltersAndSort(
  projects: ProjectRecord[],
  filters: ProjectsFiltersState
): ProjectRecord[] {
  // Filter
  const filtered = projects.filter((project) => {
    // Search query filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesSearch =
        project.projectName.toLowerCase().includes(query) ||
        project.organizationName.toLowerCase().includes(query) ||
        project.executiveSummary.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Country filter
    if (filters.selectedCountries.length > 0) {
      if (!project.country.some((c) => filters.selectedCountries.includes(c))) return false;
    }

    // Organisation filter (multi-select)
    if (filters.selectedOrganizations.length > 0) {
      if (!filters.selectedOrganizations.includes(project.organizationName)) return false;
    }

    // Sector filter
    if (filters.selectedSectors.length > 0) {
      if (!filters.selectedSectors.includes(project.sector)) return false;
    }

    // Product group filter
    if (filters.selectedProductGroups.length > 0) {
      if (!project.productGroup || !filters.selectedProductGroups.includes(project.productGroup)) return false;
    }

    // CFP filter
    if (filters.cfpMode === 'hasCfp') {
      if (!project.callForProposalLink) return false;
    }

    return true;
  });

  // Sort
  return [...filtered].sort((a, b) => {
    switch (filters.sortMode) {
      case 'newest':
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      case 'endDate':
        return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
      case 'relevance':
      default:
        return 0;
    }
  });
}

export function buildActiveFilterChips(filters: ProjectsFiltersState): FilterChip[] {
  const chips: FilterChip[] = [];

  if (filters.searchQuery) {
    chips.push({ key: 'search', label: `Search: "${filters.searchQuery}"` });
  }

  for (const country of filters.selectedCountries) {
    chips.push({ key: `country:${country}`, label: `Country: ${country}` });
  }

  for (const org of filters.selectedOrganizations) {
    chips.push({ key: `org:${org}`, label: `Org: ${org}` });
  }

  for (const sector of filters.selectedSectors) {
    chips.push({ key: `sector:${sector}`, label: `Sector: ${sector}` });
  }

  for (const pg of filters.selectedProductGroups) {
    const label = isValidProductGroupKey(pg) ? getProductGroupLabel(pg) : pg;
    chips.push({ key: `productGroup:${pg}`, label: `Product: ${label}` });
  }

  if (filters.cfpMode === 'hasCfp') {
    chips.push({ key: 'cfp', label: 'CFP: Yes' });
  }

  return chips;
}

export function removeChip(
  filters: ProjectsFiltersState,
  chipKey: string
): ProjectsFiltersState {
  if (chipKey === 'search') {
    return { ...filters, searchQuery: '' };
  }

  if (chipKey === 'cfp') {
    return { ...filters, cfpMode: 'any' };
  }

  if (chipKey.startsWith('country:')) {
    const country = chipKey.slice('country:'.length);
    return {
      ...filters,
      selectedCountries: filters.selectedCountries.filter((c) => c !== country),
    };
  }

  if (chipKey.startsWith('org:')) {
    const org = chipKey.slice('org:'.length);
    return {
      ...filters,
      selectedOrganizations: filters.selectedOrganizations.filter((o) => o !== org),
    };
  }

  if (chipKey.startsWith('sector:')) {
    const sector = chipKey.slice('sector:'.length);
    return {
      ...filters,
      selectedSectors: filters.selectedSectors.filter((s) => s !== sector),
    };
  }

  if (chipKey.startsWith('productGroup:')) {
    const pg = chipKey.slice('productGroup:'.length);
    return {
      ...filters,
      selectedProductGroups: filters.selectedProductGroups.filter((p) => p !== pg),
    };
  }

  return filters;
}

export function formatDateNL(isoString: string | null | undefined): string {
  if (!isoString) return '—';
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '—';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatRangeNL(startIso: string | null | undefined, endIso: string | null | undefined): string {
  const start = formatDateNL(startIso);
  const end = formatDateNL(endIso);
  if (start === '—' && end === '—') return '—';
  return `${start} – ${end}`;
}

export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;

  return formatDateNL(isoString);
}
